import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Bot, MessageSquare, User } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

function RobotAvatar({ isHovered }: { isHovered: boolean }) {
 return (
 <motion.div 
 className="relative w-full h-72 flex items-center justify-center"
 animate={{
 filter: isHovered 
 ? 'drop-shadow(0 8px 24px rgba(0, 0, 0, 0.15))' 
 : 'drop-shadow(0 8px 20px rgba(0, 0, 0, 0.3))',
 }}
 transition={{ duration: 0.5 }}
 >
 <svg 
 viewBox="0 0 200 200" 
 className="w-64 h-64"
 >
 <defs>
 {/* Gradient for robot body - light mode */}
 <linearGradient id="robotBodyLight" x1="0%" y1="0%" x2="100%" y2="100%">
 <stop offset="0%" stopColor="#f0f4f8" />
 <stop offset="30%" stopColor="#e8f4f8" />
 <stop offset="60%" stopColor="#d0f0e8" />
 <stop offset="100%" stopColor="#e0e8f0" />
 </linearGradient>
 {/* Gradient for robot body - dark mode */}
 <linearGradient id="robotBodyDark" x1="0%" y1="0%" x2="100%" y2="100%">
 <stop offset="0%" stopColor="#3d4055" />
 <stop offset="30%" stopColor="#454860" />
 <stop offset="60%" stopColor="#3a4550" />
 <stop offset="100%" stopColor="#353850" />
 </linearGradient>
 {/* Face panel gradient */}
 <linearGradient id="facePanel" x1="0%" y1="0%" x2="0%" y2="100%">
 <stop offset="0%" stopColor="#1a1a2e" />
 <stop offset="50%" stopColor="#16162a" />
 <stop offset="100%" stopColor="#121226" />
 </linearGradient>
 {/* Antenna glow */}
 <radialGradient id="antennaGlow" cx="50%" cy="50%" r="50%">
 <stop offset="0%" stopColor={isHovered ? "#a5b4fc" : "#6b7280"} />
 <stop offset="100%" stopColor={isHovered ? "#818cf8" : "#4b5563"} />
 </radialGradient>
 </defs>
 
 {/* Antenna pole */}
 <rect 
 x="96" y="12" width="8" height="28" rx="4"
 fill={isHovered ? "url(#robotBodyLight)" : "url(#robotBodyDark)"}
 />
 
 {/* Antenna ball */}
 <circle 
 cx="100" cy="10" r="8"
 fill="url(#antennaGlow)"
 />
 
 {/* Main head - rounded rectangle */}
 <rect 
 x="30" y="40" width="140" height="130" rx="65"
 fill={isHovered ? "url(#robotBodyLight)" : "url(#robotBodyDark)"}
 />
 
 {/* Left ear */}
 <ellipse 
 cx="28" cy="105" rx="12" ry="16"
 fill={isHovered ? "#c7d2fe" : "#4b5563"}
 />
 
 {/* Right ear */}
 <ellipse 
 cx="172" cy="105" rx="12" ry="16"
 fill={isHovered ? "#c7d2fe" : "#4b5563"}
 />
 
 {/* Face panel (dark oval) */}
 <ellipse 
 cx="100" cy="105" rx="55" ry="48"
 fill="url(#facePanel)"
 />
 
 {/* Left eye */}
 <rect 
 x="72" y="88" width="14" height="32" rx="7"
 fill="#ffffff"
 >
 {isHovered && (
 <animate 
 attributeName="height" 
 values="32;4;32" 
 dur="3s" 
 repeatCount="indefinite"
 keyTimes="0;0.05;0.1"
 calcMode="spline"
 keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
 />
 )}
 </rect>
 
 {/* Right eye */}
 <rect 
 x="114" y="88" width="14" height="32" rx="7"
 fill="#ffffff"
 >
 {isHovered && (
 <animate 
 attributeName="height" 
 values="32;4;32" 
 dur="3s" 
 repeatCount="indefinite"
 keyTimes="0;0.05;0.1"
 calcMode="spline"
 keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
 />
 )}
 </rect>
 </svg>
 </motion.div>
 );
}

function AttendflowCard({ 
 title, subtitle, account, module, chatLabel, hoverLabel 
}: { 
 title: string; subtitle: string; account: string; module: string; chatLabel: string; hoverLabel: string;
}) {
 const cardRef = useRef<HTMLDivElement>(null);
 const [isHovered, setIsHovered] = useState(false);
 const [mousePosition, setMousePosition] = useState({ x: 190, y: 260 });

 const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
 if (!cardRef.current) return;
 const rect = cardRef.current.getBoundingClientRect();
 setMousePosition({
 x: e.clientX - rect.left,
 y: e.clientY - rect.top,
 });
 };

 return (
 <div className="flex justify-center items-center gap-6 md:-ml-24">
 {/* Hover Me Arrow - Left side */}
 <motion.div
 className="hidden md:flex flex-col items-end"
 initial={{ opacity: 0, x: -20 }}
 whileInView={{ opacity: 1, x: 0 }}
 viewport={{ once: true }}
 animate={isHovered ? { opacity: 0.3 } : { opacity: 1 }}
 >
 <motion.span 
 className="text-lg font-medium italic mb-2"
 style={{ color: 'hsl(170 55% 45%)' }}
 animate={{ x: [0, 5, 0] }}
 transition={{ duration: 1.5, repeat: Infinity }}
 >
 {hoverLabel}
 </motion.span>
 <svg width="60" height="40" viewBox="0 0 60 40" style={{ color: 'hsl(170 55% 45%)' }}>
 <motion.path
 d="M5,35 Q25,35 35,20 Q45,5 55,8"
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 strokeLinecap="round"
 initial={{ pathLength: 0 }}
 animate={{ pathLength: 1 }}
 transition={{ duration: 1, delay: 0.5 }}
 />
 <motion.path
 d="M48,3 L56,8 L50,14"
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 strokeLinecap="round"
 strokeLinejoin="round"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 1.3 }}
 />
 </svg>
 </motion.div>

 {/* Main Card */}
 <motion.div
 ref={cardRef}
 className="relative w-full max-w-[380px] h-[520px] rounded-3xl overflow-hidden cursor-pointer"
 style={{
 boxShadow: isHovered 
 ? '0 20px 60px -10px rgba(168, 85, 247, 0.4), 0 15px 40px -15px rgba(59, 130, 246, 0.35), 0 10px 30px -10px rgba(236, 72, 153, 0.3)'
 : '0 15px 50px -10px rgba(139, 92, 246, 0.25), 0 10px 35px -15px rgba(99, 102, 241, 0.2), 0 8px 25px -10px rgba(168, 85, 247, 0.15)',
 }}
 onMouseEnter={() => setIsHovered(true)}
 onMouseLeave={() => setIsHovered(false)}
 onMouseMove={handleMouseMove}
 initial={{ opacity: 0, y: 30 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 whileHover={{ scale: 1.02 }}
 transition={{ duration: 0.4 }}
 >
 {/* Animated rainbow border on hover */}
 <motion.div
 className="absolute inset-0 rounded-3xl"
 style={{
 padding: '2px',
 background: isHovered
 ? 'linear-gradient(135deg, #a855f7 0%, #6366f1 20%, #3b82f6 40%, #06b6d4 60%, #ec4899 80%, #a855f7 100%)'
 : 'linear-gradient(135deg, rgba(71, 85, 105, 0.5) 0%, rgba(51, 65, 85, 0.3) 100%)',
 backgroundSize: isHovered ? '300% 300%' : '100% 100%',
 }}
 animate={isHovered ? {
 backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
 } : { backgroundPosition: '0% 0%' }}
 transition={{
 duration: 4,
 repeat: Infinity,
 ease: "linear",
 }}
 >
 {/* Inner card */}
 <div className="absolute inset-[2px] rounded-[22px] overflow-hidden">
 {/* Base gradient background - changes to white on hover */}
 <motion.div 
 className="absolute inset-0"
 animate={{
 background: isHovered 
 ? 'linear-gradient(180deg, #ffffff 0%, #f8fafc 30%, #f1f5f9 60%, #e2e8f0 100%)'
 : 'linear-gradient(180deg, #1e2235 0%, #252a40 15%, #1d2133 45%, #181b28 75%, #14161f 100%)',
 }}
 transition={{ duration: 0.5 }}
 />
 
 {/* Rainbow aurora effects from top - visible on hover */}
 {isHovered && (
 <>
 {/* Main rainbow gradient from top-right */}
 <motion.div
 className="absolute inset-0"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ duration: 0.4 }}
 style={{
 background: `
 radial-gradient(ellipse 120% 80% at 70% -10%, 
 rgba(255, 0, 0, 0.5) 0%, 
 rgba(255, 165, 0, 0.45) 15%, 
 rgba(255, 255, 0, 0.4) 25%, 
 rgba(0, 255, 0, 0.35) 40%, 
 rgba(0, 200, 255, 0.3) 55%, 
 rgba(0, 100, 255, 0.25) 70%,
 transparent 90%
 )
 `,
 }}
 />
 
 {/* Secondary rainbow ray */}
 <motion.div
 className="absolute inset-0"
 initial={{ opacity: 0 }}
 animate={{ opacity: 0.6 }}
 transition={{ duration: 0.5, delay: 0.1 }}
 style={{
 background: `
 linear-gradient(135deg, 
 rgba(255, 50, 50, 0.3) 0%, 
 rgba(255, 200, 50, 0.25) 20%, 
 rgba(50, 255, 100, 0.25) 40%, 
 rgba(50, 200, 255, 0.2) 60%,
 transparent 80%
 )
 `,
 }}
 />

 {/* Bottom left corner glow - blue/cyan */}
 <motion.div
 className="absolute bottom-0 left-0 w-72 h-72"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ duration: 0.4, delay: 0.15 }}
 style={{
 background: 'radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.5) 0%, rgba(6, 182, 212, 0.3) 30%, transparent 70%)',
 }}
 />
 
 {/* Bottom right corner glow - pink/magenta */}
 <motion.div
 className="absolute bottom-0 right-0 w-72 h-72"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ duration: 0.4, delay: 0.2 }}
 style={{
 background: 'radial-gradient(circle at bottom right, rgba(236, 72, 153, 0.45) 0%, rgba(168, 85, 247, 0.3) 30%, transparent 70%)',
 }}
 />

 {/* Center light behind silhouette */}
 <motion.div
 className="absolute top-1/3 left-1/2 -translate-x-1/2 w-64 h-64"
 initial={{ opacity: 0, scale: 0.8 }}
 animate={{ opacity: 0.8, scale: 1 }}
 transition={{ duration: 0.5 }}
 style={{
 background: 'radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 200, 0.4) 40%, transparent 70%)',
 }}
 />
 </>
 )}
 
 {/* Dark mode: Top aurora/glow effect */}
 {!isHovered && (
 <motion.div 
 className="absolute inset-0"
 animate={{
 opacity: [0.3, 0.5, 0.3],
 }}
 transition={{
 duration: 4,
 repeat: Infinity,
 ease: "easeInOut",
 }}
 style={{
 background: 'radial-gradient(ellipse 80% 40% at 50% 5%, rgba(130, 120, 170, 0.2) 0%, rgba(100, 100, 150, 0.08) 40%, transparent 70%)',
 }}
 />
 )}

 {/* Dark mode: Flowing wave layers */}
 {!isHovered && [0, 1, 2].map((i) => (
 <motion.div
 key={i}
 className="absolute inset-0"
 style={{
 background: `linear-gradient(${140 + i * 45}deg, 
 rgba(100, 100, 140, ${0.05 - i * 0.012}) 0%, 
 rgba(120, 110, 150, ${0.03 - i * 0.008}) 50%,
 transparent 100%)`,
 filter: 'blur(35px)',
 }}
 animate={{
 x: [0, 30 + i * 12, 0, -30 - i * 12, 0],
 y: [0, -18 - i * 7, 12, 18 + i * 6, 0],
 }}
 transition={{
 duration: 10 + i * 4,
 repeat: Infinity,
 ease: "easeInOut",
 delay: i * 0.8,
 }}
 />
 ))}

 {/* Mouse-following glow on hover */}
 {isHovered && (
 <motion.div
 className="absolute w-48 h-48 rounded-full pointer-events-none"
 style={{
 background: 'radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, rgba(255, 220, 100, 0.3) 40%, transparent 70%)',
 filter: 'blur(25px)',
 left: mousePosition.x - 96,
 top: mousePosition.y - 96,
 }}
 initial={{ opacity: 0, scale: 0.6 }}
 animate={{ opacity: 0.7, scale: 1 }}
 transition={{ duration: 0.35 }}
 />
 )}
 </div>
 </motion.div>

 {/* Card Content */}
 <div className="relative z-10 h-full flex flex-col p-6 pt-8">
 {/* Title Section */}
 <div className="text-center mb-2">
 <motion.h3
 className="text-3xl font-bold mb-2"
 animate={{
 color: isHovered ? 'rgba(120, 120, 140, 0.95)' : 'rgba(161, 161, 170, 0.9)',
 }}
 transition={{ duration: 0.4 }}
 >
 {title}
 </motion.h3>
 <motion.p 
 className="text-base font-medium"
 animate={{
 color: isHovered ? 'rgba(100, 100, 120, 0.9)' : 'rgba(161, 161, 170, 0.8)',
 }}
 transition={{ duration: 0.4 }}
 >
 {subtitle}
 </motion.p>
 </div>

 {/* Robot Avatar */}
 <div className="flex-1 flex items-center justify-center">
 <RobotAvatar isHovered={isHovered} />
 </div>

 {/* Bottom Account Bar - Glass morphism style */}
 <motion.div 
 className="flex items-center justify-between gap-2 p-3 rounded-2xl backdrop-blur-md overflow-hidden"
 animate={{
 background: isHovered 
 ? 'rgba(20, 30, 50, 0.85)' 
 : 'rgba(30, 34, 53, 0.75)',
 borderColor: isHovered 
 ? 'rgba(100, 150, 200, 0.3)' 
 : 'rgba(71, 85, 105, 0.35)',
 }}
 style={{
 border: '1px solid',
 boxShadow: isHovered 
 ? '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)' 
 : 'inset 0 1px 0 rgba(255,255,255,0.03)',
 }}
 transition={{ duration: 0.4 }}
 >
 {/* Avatar and Account Info */}
 <div className="flex items-center gap-2 min-w-0 flex-1">
 <motion.div 
 className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
 animate={{
 background: isHovered 
 ? 'rgba(30, 40, 60, 0.8)' 
 : 'rgba(51, 65, 85, 0.6)',
 borderColor: isHovered 
 ? 'rgba(100, 150, 200, 0.3)' 
 : 'rgba(71, 85, 105, 0.35)',
 }}
 style={{
 border: '1px solid',
 }}
 transition={{ duration: 0.4 }}
 >
 <User 
 className="w-4 h-4"
 style={{ 
 color: '#94a3b8',
 }}
 />
 </motion.div>
 <div className="flex flex-col min-w-0 overflow-hidden">
 <motion.div 
 className="text-xs font-semibold whitespace-nowrap truncate"
 animate={{ color: '#ffffff' }}
 >
 {account}
 </motion.div>
 <motion.div 
 className="text-xs whitespace-nowrap truncate"
 animate={{
 color: isHovered ? 'rgba(100, 200, 180, 0.9)' : 'rgba(148, 163, 184, 0.7)',
 }}
 transition={{ duration: 0.4 }}
 >
 {module}
 </motion.div>
 </div>
 </div>

 {/* Chat Button - Teal/Cyan style on hover */}
 <motion.button
 className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap flex-shrink-0"
 animate={{
 background: isHovered 
 ? 'rgba(20, 184, 166, 0.2)' 
 : 'rgba(51, 65, 85, 0.5)',
 borderColor: isHovered 
 ? 'rgba(20, 184, 166, 0.5)' 
 : 'rgba(71, 85, 105, 0.45)',
 color: isHovered ? 'rgb(94, 234, 212)' : '#cbd5e1',
 }}
 style={{
 border: '1px solid',
 }}
 transition={{ duration: 0.4 }}
 whileHover={{ scale: 1.05 }}
 whileTap={{ scale: 0.98 }}
 >
 <MessageSquare className="w-4 h-4" />
 {chatLabel}
 </motion.button>
 </motion.div>
 </div>
 </motion.div>
 </div>
 );
}

export function DigitalEmployeeExperienceSection() {
 const { language } = useLanguage();

 const texts = {
 zh: {
 sectionTitle: "AI Agent 体验",
 sectionDesc: "智能AIAI Agent，全天候自动化处理物流业务",
 title: "你的专属 AI Agent",
 subtitle: "智能作业专属助手",
 account: "@ocean-booking-bot",
 module: "AI 作业模块",
 hoverMe: "Hover Me!",
 chat: "开始对话",
 },
 en: {
 sectionTitle: "Digital Employee Experience",
 sectionDesc: "Intelligent AI agents, automating logistics operations 24/7",
 title: "Your Exclusive Digital Employee",
 subtitle: "Smart Operations Assistant",
 account: "@ocean-booking-bot",
 module: "AI Work Module",
 hoverMe: "Hover Me!",
 chat: "Let's Chat",
 },
 th: {
 sectionTitle: "ประสบการณ์พนักงานดิจิทัล",
 sectionDesc: "พนักงาน AI อัจฉริยะ ดำเนินการโลจิสติกส์อัตโนมัติตลอด 24 ชั่วโมง",
 title: "พนักงานดิจิทัลเฉพาะของคุณ",
 subtitle: "ผู้ช่วยปฏิบัติการอัจฉริยะ",
 account: "@ocean-booking-bot",
 module: "โมดูล AI",
 hoverMe: "Hover Me!",
 chat: "เริ่มแชท",
 },
 };

 const content = texts[language];

 return (
 <section className="py-12 bg-gradient-to-b from-background to-muted/30">
 <div className="container mx-auto px-4">
 {/* Section Header */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 className="text-center mb-16"
 >
 <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-ai text-primary-foreground text-sm font-medium mb-4 shadow-ai">
 <Bot className="w-4 h-4 inline mr-2" />
 {content.sectionTitle}
 </span>
 <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
 {content.sectionTitle}
 </h2>
 <p className="text-muted-foreground max-w-2xl mx-auto">
 {content.sectionDesc}
 </p>
 </motion.div>

 {/* Card */}
 <AttendflowCard
 title={content.title}
 subtitle={content.subtitle}
 account={content.account}
 module={content.module}
 chatLabel={content.chat}
 hoverLabel={content.hoverMe}
 />
 </div>
 </section>
 );
}
